import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup} from "@mui/material";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import PrimaryButton from "../../../../../../common/components/primary_button";

import { ChecklistListaForm, ChecklistListaSchema } from "../../../schemas/Checklist_lista/Checklist_lista";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import FormContainer from "../../../../../../common/components/form_container";
import { ChecklistLista } from "../../../../domain/models/checklist_Lista";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { truncateByDomain } from "recharts/types/util/ChartUtils";


type Props = {
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (form: ChecklistListaForm) => void;
  selectedChecklist: ChecklistLista | undefined;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
};

export const ModalChecklistLista = ({ open, onClose, saving, onSubmit, selectedChecklist, onOpenChangeHistory }: Props) => {

  const hookForm = useForm<ChecklistListaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ChecklistListaSchema),
  });

  useEffect(() => {
    if (selectedChecklist) {
      hookForm.setValue("idChecklist", selectedChecklist.IdChecklist);
      hookForm.setValue("nome", selectedChecklist.Nome);
    } else {
      hookForm.reset({statusCheckList: true});
    }
  }, [hookForm, open, selectedChecklist]);

  const handleSubmit = useCallback(
    (form: ChecklistListaForm) => {
      if (!saving) onSubmit(form);
    },
    [saving, onSubmit]
  );

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  const handleChangeHistory = useCallback(() => {
    if (selectedChecklist) {
      onOpenChangeHistory(selectedChecklist.IdChecklist, LogTipoTabela.CHECKLIST);
    }
  }, [selectedChecklist, onOpenChangeHistory]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            component: "form",
            onSubmit: hookForm.handleSubmit(handleSubmit),
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": { width: "100%", maxWidth: "40rem" },
          },
        }}
      >
        <DialogTitle>{selectedChecklist ? "Editar Check List" : "Cadastrar Check List"}</DialogTitle>
        <DialogContent>
           <ModalLoading loading={saving}>
            <FormContainer title="Nome do Check List" arrangement="fullRow">
              <>
                <Controller
                  name='statusCheckList'
                  control={hookForm.control}
                  render={({ field }) => (
                    <FormControl size="small" className="grid-col-1" required={true}>
                      <FormLabel id="statusCheckList" required={true}>
                        Status do Check List
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby="statusCheckList"
                        name="statusCheckList"
                        row={true}
                        value={field.value !== undefined ? String(field.value) : "true"}
                        onChange={(e) => { 
                          if (!saving) {
                            field.onChange(e.target.value === "true"); 
                          }
                        }}>

                        <FormControlLabel
                          className="text-black dark:text-white"
                          value="true" 
                          control={<Radio />}
                          label="Ativo" />

                        <FormControlLabel
                          className="text-black dark:text-white"
                          value="false"
                          control={<Radio />}
                          label="Inativo" />

                      </RadioGroup>
                    </FormControl>
                  )} 
                />
                <DefaultFormInput name="nome" label="Item" reactHookForm={hookForm} required={false} />
              </>
            </FormContainer>
          </ModalLoading>
        </DialogContent>
          <DialogActions>
              <div className="w-full h-full items-center justify-between flex p-4 gap-4">
                <div>
                  {
                    isNotNullOrUndefined(selectedChecklist) &&
                    <PrimaryButton
                      buttonColor="yellow"
                      onClick={handleChangeHistory}>
                      <span className="text-xs">
                        Historico de alterações
                      </span>
                    </PrimaryButton>
                  }
                </div>
    
                <div className="flex flex-row gap-2">
                  <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                    <h1
                      className="text-sm text-black dark:text-white"
                      onClick={onClose}>
                      Cancelar
                    </h1>
                  </div>
    
                  <div className="w-[120px]">
                    {saving ? (
                      <PrimaryButton
                        buttonColor="primary-green">
                        Salvando...
                      </PrimaryButton>
                    ) : (
                      <PrimaryButton
                        type="submit"
                        buttonColor="primary-green">
                        Salvar
                      </PrimaryButton>
                    )}
                  </div>
                </div>
              </div>
          </DialogActions>
      </Dialog>
    </>
  );
};
